<template>
  <div class="post-container" :style="fontFamilyStyle">
    <Header v-bind="docHeader" />
    <div class="post-content-wrapper">
      <article class="post-article" v-if="docDetail.content">
        <SiderNav :categories="docCategories" :active-id="activeId" />
        <Content
          class="max-w-[784px]"
          :content="docDetail.content"
          @current-heading="activeId = $event"
        />
      </article>
    </div>
    <FurtherMore :cards="docDetail?.recommended_data_platform" />
    <footer class="w-full">
      <StartRightNow :retractable="false" :handler="handleTrail" />
      <AppFooter :retractable="false" />
    </footer>
  </div>
</template>

<script setup lang="ts" name="PostDetail">
// 博客详情页
import { getHelpCategoryDoc } from '~/api/module/help-center';
import StartRightNow from '~/design/widgets/start-right-now/index.vue';
import { useWatchPageSeoData } from '~/hooks/useSeo';
import Content from './components/content.vue';
import FurtherMore from './components/further-more/index.vue';
import Header from './components/header.vue';
import SiderNav from './components/sider-nav.vue';
import { getFallbackText, parseContent } from './utils';

// 路由相关
const route = useRoute();
const blogPath = computed(() => String(route.params.post));
const activeId = ref<string>(''); // 当前激活的标题ID

const is_CN = computed(() => {
  return isChinese();
});

const AppFooter = defineAsyncComponent(() =>
  isChinese()
    ? import('~/design/widgets/bottom-footer/footer-cn.vue').then(m => m.default)
    : import('~/design/widgets/bottom-footer/footer-en.vue').then(m => m.default),
);

const handleTrail = () => {
  useUserInfo().value
    ? navigateTo('/workspace')
    : navigateTo(
        `/login?redirect=${encodeURIComponent(encodeURIComponent(`${location.origin}/workspace`))}`,
      );
};

const { data: docData } = await useAsyncData(`docData-${route.params.post}`, async () => {
  const params = {
    path_url: blogPath.value,
    is_copy: false,
  };
  try {
    const res = await getHelpCategoryDoc(params);

    if (!res?.data || res?.data.length === 0) {
      return null;
    }

    const docDetail: any = res?.data?.[0] || {};

    if (!docDetail || !docDetail.content) {
      return null;
    }

    return { ...res, render: parseContent(docDetail?.content) };
  } catch (err) {
    console.error('Error fetching document data:', err);
    return null;
  }
});

const fontFamilyStyle = computed(() => {
  return is_CN.value ? {} : { fontFamily: 'Poppins, sans-serif' };
});

const docDetail: any = computed(() => {
  return docData.value?.data?.[0] || {};
});

const docCategories: any = computed(() => {
  return docData.value?.render;
});

const docHeader = computed(() => {
  return {
    title: docDetail.value?.main_title,
    desc: docDetail.value?.sub_title,
    source: docDetail.value?.creator_name,
    cover: docDetail.value?.first_image_url,
    sourceIcon: docDetail.value?.creator_avatar_url,
    publishDate: docDetail.value?.create_time,
    readDuration: docDetail.value?.read_duration,
    tags: docDetail.value?.tag_list,
  };
});

const seoData = computed(() => {
  let fallbackText = '';
  try {
    fallbackText = getFallbackText(docDetail.value?.content);
  } catch (err) {
    console.error(err);
  }
  return {
    seo_title: docDetail.value?.seo_title || docDetail.value?.name,
    seo_description: docDetail.value?.seo_description || fallbackText,
    seo_keywords: docDetail.value?.seo_keywords,
  };
});

// 页面挂载时滚动到顶部
onMounted(() => {
  const postContainer = document.querySelector('.post-container');
  if (postContainer) {
    postContainer.scrollIntoView({ behavior: 'instant', block: 'start' });
  }
});

useWatchPageSeoData(seoData);
</script>

<style lang="scss" scoped>
.post-container {
  @apply flex-col items-center;
  width: 100vw;
  min-width: 1200px;
}
.post-content-wrapper {
  max-width: 1200px;
  width: 100%;
}
.post-article {
  @apply relative flex pb-[128px] justify-between;
  max-width: 1200px;
  width: 100%;
}
</style>
<style lang="scss">
html[lang='en-US'] {
  font-family: 'Poppins', sans-serif;
  body {
    font-family: 'Poppins', sans-serif;
  }
}
</style>
